import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, BookOpen, ExternalLink, ShoppingBag } from "lucide-react";
import { HeroPortrait } from "@/components/masterclass/HeroPortrait";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { HeroQuote } from "@/components/site/RotatingQuote";
import { Reveal } from "@/hooks/useReveal";
import { trackCTA } from "@/lib/tracking";
import { trackHeroCTAClick } from "@/lib/hero-analytics";
// Hero, mid-page, final band and the mobile sticky bar all render this exact
// label + destination. See src/content/primary-cta.ts.
import { PRIMARY_CTAS } from "@/content/primary-cta";
import { trackPlacementCTA } from "@/lib/cta-placement";

const HOME_PRIMARY = PRIMARY_CTAS.home;
import { useCTAImpression } from "@/hooks/useCTAImpression";

// Below-the-fold sections are code-split so the hero can paint fast.
// Each import becomes its own chunk fetched lazily on scroll.
const MarqueeReviews = lazy(() =>
  import("@/components/site/MarqueeReviews").then((m) => ({ default: m.MarqueeReviews })));
const AuthorityBar = lazy(() =>
  import("@/components/site/AuthorityBar").then((m) => ({ default: m.AuthorityBar })));
const CredibilityStrip = lazy(() =>
  import("@/components/site/CredibilityStrip").then((m) => ({ default: m.CredibilityStrip })));
const LogoWall = lazy(() =>
  import("@/components/site/LogoWall").then((m) => ({ default: m.LogoWall })));
const TrustCounters = lazy(() =>
  import("@/components/site/TrustCounters").then((m) => ({ default: m.TrustCounters })));
const CompetitorMatrix = lazy(() =>
  import("@/components/site/CompetitorMatrix").then((m) => ({ default: m.CompetitorMatrix })));
const MakeChangePillars = lazy(() =>
  import("@/components/site/MakeChangePillars").then((m) => ({ default: m.MakeChangePillars })));
const TestimonialSpotlight = lazy(() =>
  import("@/components/site/TestimonialSpotlight").then((m) => ({ default: m.TestimonialSpotlight })));
const TestimonialCarousel = lazy(() =>
  import("@/components/site/TestimonialCarousel").then((m) => ({ default: m.TestimonialCarousel })));
const ReviewsStrip = lazy(() =>
  import("@/components/site/ReviewsStrip").then((m) => ({ default: m.ReviewsStrip })));
const ProofAndOutcomes = lazy(() =>
  import("@/components/site/ProofAndOutcomes").then((m) => ({ default: m.ProofAndOutcomes })));
const ConversionBand = lazy(() =>
  import("@/components/site/ConversionBand").then((m) => ({ default: m.ConversionBand })));
const PricingCards = lazy(() =>
  import("@/components/site/PricingCards").then((m) => ({ default: m.PricingCards })));
const LeadMagnetForm = lazy(() =>
  import("@/components/site/LeadMagnetForm").then((m) => ({ default: m.LeadMagnetForm })));
const FAQSection = lazy(() =>
  import("@/components/site/FAQSection").then((m) => ({ default: m.FAQSection })));
import { HOME_FAQ, buildFAQPageJsonLd } from "@/content/faq";

import {
  AUDIENCES,
  BOOKS,
  ECOSYSTEM,
  MAKECHANGE_OS,
  PHONE_DISPLAY,
  PHONE_HREF,
  PROBLEMS,
  SERVICES,
  STATS,
  TICKER,
} from "@/content/site";
// vite-imagetools builds optimized, multi-width AVIF + WebP variants of the
// hero at compile time. `as=srcset` returns a ready-to-use srcset string with
// width descriptors, so the browser can pick the smallest file that fits the
// rendered slot on the current device + DPR — critical for mobile LCP.
// Baseline <img src>. Deliberately a bounded 1024w JPEG variant rather than
// the 1.2 MB studio original: browsers that ignore <source>/srcset (and most
// crawlers/social scrapers) fetch exactly this file, and the original would
// otherwise also be emitted into the build output for no visual gain.
import heroImg from "../assets/dr-sully-hero-blend.jpg?format=jpg&quality=76&w=1024";
// Include 320/360/640 widths so low-DPR phones on slow networks fetch a
// tiny (~15–35 KB) hero variant instead of a desktop-sized asset. Crop
// behaviour (object-position) is unaffected — only bytes on the wire change.
import heroAvifSrcSet from "../assets/dr-sully-hero-blend.jpg?format=avif&quality=60&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import heroWebpSrcSet from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=70&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import heroJpgSrcSet from "../assets/dr-sully-hero-blend.jpg?format=jpg&quality=76&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import { PRICING_LADDER_SUMMARY } from "@/content/offers";
// Tiny (~1–2 KB) blurred LQIP for the blur-up placeholder. Loaded inline as
// a URL rather than base64 because vite-imagetools guarantees compression;
// the browser fetches it in parallel with the preloaded hero and it's ready
// before the sharp image decodes.
import heroPlaceholder from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=40&w=24";

// Responsive LCP preload. Instead of hard-coding one width, we hand the
// browser the same srcset the <picture> will render and let it pick the
// smallest variant that fits the current viewport + DPR. Mobile devices
// pull ~15–25 KB; desktops upgrade to 1024/1360w — from a single hint.
// We preload AVIF when the browser can decode it (matching what <picture>
// will actually choose) and WebP otherwise, so the preload never fetches a
// format the page then discards.
import heroPreloadFallback from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=70&w=640";
import heroPreloadSrcSet from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=70&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import heroPreloadAvifFallback from "../assets/dr-sully-hero-blend.jpg?format=avif&quality=60&w=640";
import heroPreloadAvifSrcSet from "../assets/dr-sully-hero-blend.jpg?format=avif&quality=60&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import { IMAGE_ALT } from "@/content/image-alt";
import {
  makeChangeOrganizationNode,
  organizationNode,
  personNode,
} from "@/lib/entity-jsonld";


// Single source of truth for the hero `sizes` contract. The preload hint and
// the rendered <picture> MUST use the identical string — if they diverge the
// browser preloads one candidate and then downloads a second one for the
// actual <img>, doubling hero bytes on mobile.
const HERO_SIZES =
  "(min-width: 1280px) 480px, (min-width: 1024px) 40vw, (min-width: 640px) 70vw, 100vw";

/** Synchronous AVIF encode/decode support probe (cheap, cached per module). */
const supportsAvif = (): boolean => {
  try {
    const c = document.createElement("canvas");
    c.width = c.height = 1;
    return c.toDataURL("image/avif").startsWith("data:image/avif");
  } catch {
    return false;
  }
};

// Reserves vertical rhythm so lazy sections don't shift layout while their
// chunks stream in. Height picked from average section footprint on desktop.
const SectionSkeleton = ({ minHeight = 320 }: { minHeight?: number }) => (
  <div aria-hidden style={{ minHeight }} />
);

/**
 * Inject a high-priority preload hint for the hero portrait as early as
 * possible. Done at module-eval time (not in useEffect) so the browser sees
 * the request before React hydrates — meaningfully improves LCP on cold
 * mobile loads. Guarded for SSR and against duplicate injection during HMR.
 *
 * Graceful fallback: if the preloaded asset 404s or the network drops the
 * response, the `onerror` handler removes the hint (silencing the console
 * "was preloaded but not used" warning) and lets `HeroPortrait`'s built-in
 * failed-state UI (branded initials card) take over without a broken image.
 */
if (typeof document !== "undefined" && !document.getElementById("hero-preload")) {
  const avif = supportsAvif();
  const link = document.createElement("link");
  link.id = "hero-preload";
  link.rel = "preload";
  link.as = "image";
  link.type = avif ? "image/avif" : "image/webp";
  link.href = avif ? heroPreloadAvifFallback : heroPreloadFallback;
  // `imagesrcset` / `imagesizes` = the responsive-preload contract. Older
  // browsers ignore them and fall back to the single `href` above.
  link.setAttribute("imagesrcset", avif ? heroPreloadAvifSrcSet : heroPreloadSrcSet);
  link.setAttribute("imagesizes", HERO_SIZES);
  link.setAttribute("fetchpriority", "high");
  link.onerror = () => link.remove();
  document.head.appendChild(link);
}




const Index = () => {
  const heroCtaRef = useCTAImpression<HTMLDivElement>("home_hero");
  const midCtaRef = useCTAImpression<HTMLDivElement>("home_scan_section");
  const finalCtaRef = useCTAImpression<HTMLDivElement>("home_close");
  return (

    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Dr. Sully | Industrial-Organizational Psychology, Executive Consulting and Speaking"
        description="Dr. RD 'Dr. Sully' Sullivan helps leaders, organizations, ministries, and teams diagnose people and systems problems, resolve conflict, and implement sustainable change."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            // Canonical entity trio: Dr. Sully (Person), DrSullySpeaks (Org),
            // and MakeChange.ai (Brand/Org) — all cross-linked by @id.
            personNode(),
            organizationNode(),
            makeChangeOrganizationNode(),
            {

              "@type": "ProfessionalService",
              "@id": "https://drsullyspeaks.com/#business",
              name: "DrSullySpeaks — He Speaks Organic Coaching & Consulting LLC",
              url: "https://drsullyspeaks.com/",
              image: "https://drsullyspeaks.com/favicon.png",
              priceRange: "$$$",
              telephone: "+1-855-762-6224",
              areaServed: "Worldwide",
              address: {
                "@type": "PostalAddress",
                addressLocality: "San Antonio",
                addressRegion: "TX",
                addressCountry: "US",
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+1-855-762-6224",
                contactType: "booking",
                areaServed: "Worldwide",
                availableLanguage: ["English"],
              },
            },
            {
              "@type": "Service",
              serviceType: "Executive Coaching",
              name: "DrSullySpeaks Executive Coaching",
              provider: { "@id": "https://drsullyspeaks.com/#business" },
              areaServed: "Worldwide",
              url: "https://drsullyspeaks.com/inquire",
              description: "1:1 executive coaching for founders, CEOs, and faith-rooted leaders.",
            },
            {
              "@type": "Service",
              serviceType: "AI Strategy Consulting",
              name: "AI Strategy & Adoption Consulting",
              provider: { "@id": "https://drsullyspeaks.com/#business" },
              areaServed: "Worldwide",
              url: "https://drsullyspeaks.com/inquire",
              description: "Human-first AI strategy for organizations of all sizes.",
            },
            {
              "@type": "Service",
              serviceType: "Keynote Speaking",
              name: "Dr. Sully — Keynote Speaking",
              provider: { "@id": "https://drsullyspeaks.com/#business" },
              areaServed: "Worldwide",
              url: "https://drsullyspeaks.com/speaking",
              description: "Keynotes, workshops, and panels for corporate, university, government, and faith audiences.",
            },
            // Built from the SAME array the visible FAQ renders, so every
            // Question/Answer in the markup is present on the page.
            buildFAQPageJsonLd(HOME_FAQ, "https://drsullyspeaks.com/#faq"),
          ],
        }}
      />

      <SiteNav />


      {/* HERO */}
      <section data-hero-section className="relative bg-hero pt-32 md:pt-36 pb-20 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-[0.04] pointer-events-none select-none">
          <div className="font-display font-bold uppercase tracking-[0.5em] text-white whitespace-nowrap text-[20vw] leading-none mt-10">
            MAKE&nbsp;CHANGE
          </div>
        </div>

        <div className="container relative grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal variant="up">
              <div className="inline-flex items-center gap-3 border border-gold/30 bg-navy-elevated/50 px-3 py-1.5 rounded-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-orange shadow-[0_0_10px_hsl(var(--orange))]" />
                <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-silver">
                  Industrial-Organizational Psychology · Leadership and Systems Change
                </span>
              </div>
            </Reveal>

            <Reveal variant="up" delay={80}>
              <h1 className="font-display font-bold uppercase text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-6 leading-[0.95] tracking-tight">
                Make Sense.<br />
                Make Systems.<br />
                <span className="text-gradient-gold">Make Change.</span>
              </h1>
            </Reveal>

            <Reveal variant="fade" delay={160}>
              <p className="text-silver text-base md:text-lg mt-7 max-w-xl leading-relaxed">
                Discover the leadership, people, and systems barriers limiting
                your progress, then identify the clearest next step forward.
              </p>
            </Reveal>

            <Reveal variant="fade" delay={200}>
              <ul className="mt-5 flex flex-wrap gap-x-2 gap-y-2 max-w-xl" aria-label="Credentials">
                {["PsyD", "ThD", "DMin", "CERC"].map((c) => (
                  <li
                    key={c}
                    className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-gold border border-gold/40 bg-gold/[0.06] px-2.5 py-1 rounded-sm"
                  >
                    {c}
                  </li>
                ))}
                {/* TODO(verify): 375 client + 100 org counts pending final documentation. */}
                <li className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-silver/80 border border-border bg-navy-elevated/40 px-2.5 py-1 rounded-sm">
                  20+ Years · 375 Served · 100+ Orgs
                </li>
              </ul>
            </Reveal>

            <Reveal variant="up" delay={240}>
              <div ref={heroCtaRef} className="mt-9 flex flex-col sm:flex-row gap-4">
                <Link
                  to={HOME_PRIMARY.href}
                  onClick={() => {
                    trackHeroCTAClick("home_hero", "scan_cta", { destination: HOME_PRIMARY.href });
                    trackPlacementCTA("hero", HOME_PRIMARY, { surface: "home_hero" });
                  }}
                  className="group inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 shadow-cta hover:brightness-110 transition-all"
                >
                  {HOME_PRIMARY.label}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/speaking"
                  onClick={() => trackHeroCTAClick("home_hero", "speaking_cta", { destination: "/speaking" })}
                  className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 hover:bg-gold/10 hover:border-gold transition-all"
                >
                  Invite Dr. Sully to Speak
                </Link>
              </div>
              <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-silver/70">
                For executives, organizations, ministries, educators, teams &amp; communities ready for sustainable change
              </p>
            </Reveal>




            <Reveal variant="fade" delay={400}>
              <HeroQuote />
            </Reveal>
          </div>


          <div className="lg:col-span-5">
            <Reveal variant="scale" delay={120}>
              <HeroPortrait
                src={heroImg}
                avifSrcSet={heroAvifSrcSet}
                webpSrcSet={heroWebpSrcSet}
                jpgSrcSet={heroJpgSrcSet}
                placeholderSrc={heroPlaceholder}
                sizes={HERO_SIZES}
                alt={IMAGE_ALT.heroHome}
              />

            </Reveal>
          </div>
        </div>

        {/* Credential ticker */}
        <div className="relative mt-20 border-y border-border bg-navy-deep/60 overflow-hidden">
          <div className="flex gap-12 py-5 animate-[mc-marquee_60s_linear_infinite] whitespace-nowrap">
            {[...TICKER, ...TICKER].map((t, i) => (
              <span
                key={i}
                className="font-display uppercase text-xs tracking-[0.3em] text-silver/70 flex items-center gap-12"
              >
                {t}
                <span className="text-gold">·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Everything below the fold is code-split. Suspense keeps the hero
          interactive while section chunks stream in on scroll. */}
      <Suspense fallback={<SectionSkeleton minHeight={200} />}>
        {/* MARQUEE 5-STAR REVIEWS */}
        <MarqueeReviews />

        {/* CREDENTIALED AUTHORITY — niche-competitive credibility */}
        <AuthorityBar />

        {/* CREDIBILITY STRIP — untitled authority ribbon */}
        <CredibilityStrip />

        {/* LOGO WALL — local + global organizations */}
        <LogoWall />

        {/* TRUST PROOF COUNTERS — animated count-ups */}
        <TrustCounters />

        {/* COMPETITOR COMPARISON MATRIX */}
        <CompetitorMatrix />
      </Suspense>

      {/* PROBLEM SECTION — When People Problems Become System Problems */}
      <section className="py-20 md:py-28 bg-navy border-b border-border">
        <div className="container">
          <Reveal variant="up">
            <span className="eyebrow">The Diagnosis</span>
            <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05] max-w-4xl">
              When people problems<br />
              <span className="text-gradient-gold">become system problems.</span>
            </h2>
            <p className="text-silver text-base md:text-lg mt-6 max-w-3xl leading-relaxed">
              Leadership challenges rarely remain isolated. Unresolved conflict,
              unclear roles, weak communication, trauma, low trust, poor
              accountability, and inconsistent systems eventually affect
              performance, culture, retention, ministry health, and organizational
              stability. Dr. Sully helps leaders identify the root issue,
              establish the right intervention, and implement clear systems that
              support people and produce sustainable change.
            </p>
          </Reveal>

          <ul className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-border border border-border">
            {PROBLEMS.map((p, i) => (
              <Reveal as="li" key={p} variant="up" delay={i * 40}>
                <div className="bg-navy-deep p-5 md:p-6 h-full text-white/90 text-sm md:text-base flex items-start gap-3 hover:bg-navy-elevated transition-colors">
                  <span className="text-gold font-display mt-0.5" aria-hidden="true">✦</span>
                  <span>{p}</span>
                </div>
              </Reveal>
            ))}
          </ul>

        </div>
      </section>

      {/* MAKECHANGE OPERATING SYSTEM — Dr. Sully's signature IP */}
      <section className="py-24 md:py-32 bg-navy-deep border-b border-border">
        <div className="container">
          <Reveal variant="up">
            <span className="eyebrow">Signature Operating System</span>
            <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05] max-w-3xl">
              The MakeChange<br />
              <span className="text-gradient-gold">Operating System.</span>
            </h2>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              A distinct operating framework developed by Dr. Sully and
              MakeChange.ai. Three public stages, one internal implementation
              rhythm.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-px bg-border border border-border">
            {MAKECHANGE_OS.map((s, i) => (
              <Reveal key={s.stage} variant="up" delay={i * 100}>
                <div className="bg-navy p-8 md:p-10 h-full hover:bg-navy-elevated transition-colors">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                    Stage 0{i + 1}
                  </div>
                  <h3 className="font-display uppercase text-white text-2xl md:text-3xl mt-4 leading-tight">
                    {s.stage}
                  </h3>
                  <p className="text-silver text-sm md:text-base mt-4 leading-relaxed">
                    {s.desc}
                  </p>
                  <div className="mt-6 pt-4 border-t border-gold/20 text-[11px] uppercase tracking-[0.25em] text-gold">
                    Internal Rhythm · {s.op}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="mt-10 text-center text-[11px] uppercase tracking-[0.3em] text-silver/70">
            Diagnose · Design · Deploy
          </p>
        </div>
      </section>









      {/* LEAD MAGNET — beats single-CTA competitors with a stacked free offer */}
      <section id="register" className="py-20 md:py-28 bg-navy-deep border-y border-border scroll-mt-24 relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,hsl(var(--gold)/0.08),transparent_50%),radial-gradient(circle_at_80%_70%,hsl(var(--orange)/0.06),transparent_50%)]"
        />
        <div className="container relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal variant="up">
            <span className="eyebrow">Free · Instant Delivery</span>
            <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
              Steal the <span className="text-gradient-gold">framework</span><br />
              Dr. Sully teaches leaders.
            </h2>

            <p className="text-silver text-base md:text-lg mt-5 max-w-xl leading-relaxed">
              Get the MAKECHANGE Playbook PDF, a 7-day implementation mini-course,
              and a free chapter of <em>Built On Purpose</em> — all delivered to
              your inbox in the next 60 seconds.
            </p>
            <ul className="mt-7 space-y-3 text-white/90 text-sm">
              <li className="flex items-start gap-3"><span className="font-display text-gold mt-0.5">✦</span> 10-pillar Playbook (PDF) — the locked framework</li>
              <li className="flex items-start gap-3"><span className="font-display text-gold mt-0.5">✦</span> 7-day Make Change mini-course (email)</li>
              <li className="flex items-start gap-3"><span className="font-display text-gold mt-0.5">✦</span> Built On Purpose — opening chapter</li>
            </ul>
          </Reveal>

          <Reveal variant="scale" delay={120}>
            <Suspense fallback={<SectionSkeleton minHeight={480} />}>
              <LeadMagnetForm sourcePage="homepage-hero-magnet" />
            </Suspense>
          </Reveal>

        </div>
      </section>

      {/* PROOF NUMBERS */}
      <section className="py-16 md:py-20 border-b border-border">
        <div className="container grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
          {STATS.map((s, i) => (
            <Reveal key={s.label} variant="up" delay={i * 80}>
              <div className="text-center md:text-left">
                <div className="font-display text-4xl md:text-5xl text-white">
                  <span className="text-gradient-gold">{s.value}</span>
                </div>
                <div className="text-[11px] md:text-xs uppercase tracking-[0.25em] text-silver mt-2">
                  {s.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHO HE SERVES */}
      <section id="about" className="py-24 md:py-32">
        <div className="container">
          <Reveal variant="up">
            <span className="eyebrow">Who Dr. Sully Serves</span>
            <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05] max-w-3xl">
              Built for leaders who<br />
              <span className="text-gradient-gold">refuse to coast.</span>
            </h2>
          </Reveal>
          <Reveal variant="fade" delay={120}>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              Dr. Sully works with a small number of executives, organizations,
              and faith leaders each year. Every engagement is selective,
              strategic, and built to outlast the conversation.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 gap-px bg-border border border-border">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.title} variant="up" delay={i * 80}>
                <div className="bg-navy p-8 md:p-10 h-full hover:bg-navy-elevated transition-colors group">
                  <div className="flex items-start gap-4">
                    <span className="font-display text-gold text-sm mt-1">
                      0{i + 1}
                    </span>
                    <div>
                      <h3 className="font-display uppercase text-white text-xl md:text-2xl tracking-wide">
                        {a.title}
                      </h3>
                      <p className="text-silver text-sm md:text-base mt-3 leading-relaxed">
                        {a.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-32 bg-navy-deep border-y border-border">
        <div className="container">
          <Reveal variant="up">
            <span className="eyebrow">Engagements</span>
            <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05] max-w-3xl">
              Three lanes.<br />
              <span className="text-gradient-gold">One standard.</span>
            </h2>
          </Reveal>

          <div className="mt-16 space-y-px bg-border border border-border">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} variant="up" delay={i * 80}>
                <div className="bg-navy p-8 md:p-12 grid md:grid-cols-12 gap-8 items-start hover:bg-navy-elevated transition-colors">
                  <div className="md:col-span-4">
                    <div className="text-xs uppercase tracking-[0.3em] text-gold">
                      {s.eyebrow}
                    </div>
                    <h3 className="font-display uppercase text-white text-3xl md:text-4xl mt-4 leading-tight">
                      {s.title}
                    </h3>
                  </div>
                  <div className="md:col-span-5">
                    <p className="text-silver text-base leading-relaxed">{s.desc}</p>
                    <ul className="mt-5 space-y-2">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex gap-3 text-sm text-white/80">
                          <span className="text-orange mt-2 h-px w-3 bg-orange flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="md:col-span-3 md:text-right">
                    <Link
                      to={`/inquire?lane=${s.id}`}
                      onClick={() =>
                        trackCTA("strategy_session_cta_click", {
                          lane: s.id,
                          surface: "home_services",
                        })
                      }
                      className="group inline-flex items-center gap-2 text-white font-display uppercase tracking-[0.2em] text-xs border-b border-gold pb-2 hover:text-gold transition-colors"
                    >
                      {s.cta}
                      <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MAKE CHANGE SCAN */}
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div
          aria-hidden="true"
          data-a11y-decorative="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none select-none flex items-center justify-center"
        >
          <span className="font-display font-bold uppercase text-white text-[18vw] tracking-[0.05em]">
            MAKECHANGE
          </span>
        </div>

        <div className="container relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal variant="up">
              <span className="eyebrow">MakeChange.ai</span>
              <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05]">
                Find the program<br />
                hiding inside your<br />
                <span className="text-gradient-gold">expertise.</span>
              </h2>
              <p className="text-silver text-base md:text-lg mt-6 max-w-xl leading-relaxed">
                The Make Change Opportunity Scan is a short diagnostic that maps
                your leadership, ministry, education, or consulting experience
                into a fundable, AI-enabled program lane.
              </p>
              <div ref={midCtaRef} className="mt-9">
                <Link
                  to={HOME_PRIMARY.href}
                  onClick={() => trackPlacementCTA("mid_page", HOME_PRIMARY, { surface: "home_scan_section" })}
                  className="group inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 shadow-cta hover:brightness-110 transition-all"
                >
                  {HOME_PRIMARY.label}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal variant="scale" delay={120}>
              <div className="premium-card p-8">
                <div className="text-xs uppercase tracking-[0.3em] text-gold">
                  What you get
                </div>
                <ul className="mt-6 space-y-4">
                  {[
                    "A clear program lane mapped to your experience",
                    "Pathway language to position your work to funders",
                    "Next concrete move — not a sales pitch",
                  ].map((b) => (
                    <li key={b} className="flex gap-3 text-white/85 text-sm leading-relaxed">
                      <span className="font-display text-gold">✦</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="gold-divider opacity-40 my-7" />
                <p className="text-silver text-xs leading-relaxed">
                  Designed for churches, schools, nonprofits, governments, and
                  businesses. Faith-safe. Human-first.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Suspense fallback={<SectionSkeleton minHeight={400} />}>
        {/* MAKECHANGE PILLARS */}
        <MakeChangePillars />

        {/* REVIEWS STRIP — featured */}
        <ReviewsStrip />

        {/* PROOF & OUTCOMES — composite outcomes + consented quotes */}
        <ProofAndOutcomes />

        {/* TESTIMONIAL CAROUSEL — multi-card swipe/arrow paging */}
        <TestimonialCarousel />

        {/* TESTIMONIAL SPOTLIGHT — single long-form rotating headliner */}
        <TestimonialSpotlight />

        {/* PRICING — 4-tier conversion ladder */}
        <section className="border-t border-border bg-navy-deep">
          <div className="container pt-20 md:pt-28">
            <Reveal variant="up" className="max-w-3xl text-center mx-auto">
              <span className="eyebrow !mx-auto">Invest · 4 Pathways</span>
              <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05]">
                Pick your <span className="text-gradient-gold">entry point.</span>
              </h2>
              <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
                {PRICING_LADDER_SUMMARY}.
                Every pathway is built to compound into the next — start where you are.
              </p>
            </Reveal>
          </div>
          <PricingCards />
        </section>
      </Suspense>




      {/* FEATURED BOOK — AI IS NOT YOUR ENEMY */}
      {(() => {
        const featured = BOOKS.find((b) => b.featured);
        if (!featured) return null;
        return (
          <section className="py-20 md:py-28 border-t border-gold/20 bg-gradient-to-b from-navy-deep via-background to-background">
            <div className="container max-w-6xl">
              <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-10 md:gap-14 items-center">
                <Reveal variant="up">
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gold/10 blur-3xl rounded-full pointer-events-none" aria-hidden />
                    <img
                      src={featured.image}
                      alt={featured.alt}
                      loading="lazy"
                      className="relative w-full max-w-sm mx-auto aspect-[4/5] object-cover rounded-sm border border-gold/40 shadow-[0_20px_60px_-15px_hsl(var(--gold)/0.4)]"
                    />
                    <div className="absolute top-3 right-3 bg-gold text-navy-deep font-display uppercase tracking-[0.2em] text-[10px] px-3 py-1.5 rounded-sm shadow-lg">
                      New Release
                    </div>
                  </div>
                </Reveal>
                <Reveal variant="up" delay={120}>
                  <div>
                    <span className="eyebrow">New Book · Out Now</span>
                    <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.02]">
                      {featured.title.split(" ").slice(0, -2).join(" ")}{" "}
                      <span className="text-gradient-gold">
                        {featured.title.split(" ").slice(-2).join(" ")}
                      </span>
                    </h2>
                    <p className="text-orange text-sm md:text-base uppercase tracking-[0.14em] mt-4">
                      {featured.subtitle}
                    </p>
                    <p className="text-silver text-base md:text-lg mt-6 leading-relaxed max-w-xl">
                      {featured.desc}
                    </p>
                    {featured.buyLinks && (
                      <div className="mt-8 flex flex-wrap gap-3">
                        {featured.buyLinks.map((link, i) => (
                          <a
                            key={link.href}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              trackCTA("book_buy_click", {
                                book: featured.title,
                                format: link.label,
                                surface: "home_featured_book",
                              })
                            }
                            className={`inline-flex items-center gap-2 font-display uppercase tracking-[0.2em] text-xs px-6 py-3.5 transition-all ${
                              i === 0
                                ? "bg-cta text-white shadow-cta hover:brightness-110"
                                : "border border-gold text-gold hover:bg-gold hover:text-navy-deep"
                            }`}
                          >
                            <ShoppingBag className="h-3.5 w-3.5" />
                            {link.label}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ))}
                        <Link
                          to="/books"
                          onClick={() =>
                            trackCTA("book_view_all_click", {
                              surface: "home_featured_book",
                            })
                          }
                          className="inline-flex items-center gap-2 font-display uppercase tracking-[0.2em] text-xs px-5 py-3.5 text-silver hover:text-white transition-colors"
                        >
                          <BookOpen className="h-3.5 w-3.5" />
                          All Books
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                        <Link
                          to="/ebooks"
                          onClick={() =>
                            trackCTA("ebooks_view_all_click", {
                              surface: "home_featured_book",
                            })
                          }
                          className="inline-flex items-center gap-2 font-display uppercase tracking-[0.2em] text-xs px-5 py-3.5 text-silver hover:text-white transition-colors"
                        >
                          <BookOpen className="h-3.5 w-3.5" />
                          eBooks &amp; Workbooks
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    )}
                  </div>
                </Reveal>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ECOSYSTEM (compressed, secondary) */}
      <section className="py-20 md:py-24">

        <div className="container">
          <Reveal variant="up">
            <span className="eyebrow">Brand Architecture</span>
            <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05] max-w-2xl">
              One voice.<br />
              <span className="text-gradient-gold">Several movements.</span>
            </h2>
          </Reveal>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {ECOSYSTEM.map((e) => (
              <div key={e.name} className="bg-navy p-6 hover:bg-navy-elevated transition-colors">
                <div className="font-display uppercase text-white text-base tracking-wide">
                  {e.name}
                </div>
                <div className="text-silver text-xs mt-1.5">{e.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BILLIONAIRE-GRADE CLOSING CTA */}
      <section className="relative py-28 md:py-40 bg-navy-deep border-t border-gold/20 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none select-none flex items-center justify-center"
        >
          <span className="font-display font-bold uppercase text-white text-[14vw]">
            WORK&nbsp;TOGETHER
          </span>
        </div>
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(var(--gold)/0.12),transparent_60%)]"
        />

        <div className="container relative max-w-4xl text-center">
          <Reveal variant="up">
            <span className="eyebrow !justify-center mx-auto">The Next Step</span>
            <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight">
              You do not need more confusion.<br />
              <span className="text-gradient-gold">You need the right diagnosis</span><br />
              and a clear system.
            </h2>
            <p className="text-silver text-base md:text-lg mt-8 max-w-2xl mx-auto leading-relaxed">
              Start with the Make Change Scan and identify what is strengthening
              your progress, what is blocking it, and what needs to happen next.
            </p>
          </Reveal>

          <Reveal variant="up" delay={120}>
            <div ref={finalCtaRef} className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={HOME_PRIMARY.href}
                onClick={() => trackPlacementCTA("final", HOME_PRIMARY, { surface: "home_close" })}
                className="group inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-10 py-6 shadow-cta hover:brightness-110 active:scale-[0.98] transition-all"
              >
                {HOME_PRIMARY.label}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/speaking"
                onClick={() => trackCTA("speaking_cta", { surface: "home_close" })}
                className="inline-flex items-center justify-center gap-2 border border-gold/60 text-white hover:bg-gold/10 hover:border-gold active:scale-[0.98] font-display uppercase tracking-[0.2em] text-sm px-10 py-5 transition-all"
              >
                Invite Dr. Sully to Speak
              </Link>
              <a
                href={PHONE_HREF}
                onClick={() => trackCTA("phone_click", { surface: "home_close" })}
                className="inline-flex flex-col items-center justify-center border border-gold/60 text-white hover:bg-gold/10 hover:border-gold active:scale-[0.98] font-display uppercase tracking-[0.2em] text-sm px-8 py-5 transition-all leading-tight"
              >
                <span className="flex items-center gap-2"><Phone className="h-4 w-4" /> Direct Line</span>
                <span className="text-gold text-base md:text-lg font-bold tracking-[0.14em] mt-1">{PHONE_DISPLAY}</span>
              </a>
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-silver/60 mt-6">
              Replies within one business day · Faith-safe · NDA on request
            </p>
          </Reveal>
        </div>
      </section>

      <Suspense fallback={<SectionSkeleton minHeight={300} />}>
        {/* FAQ — surfaces the JSON-LD answers visibly + adds practical Qs */}
        <FAQSection />

        {/* FINAL CONVERSION BAND */}
        <ConversionBand
          onPrimaryClick={() =>
            trackPlacementCTA("final", {
              id: "conversion_band_primary",
              kind: "strategy_session",
              label: "Apply for Coaching",
              href: "/inquire?lane=coaching",
            }, { surface: "home_conversion_band" })
          }
          onSecondaryClick={() =>
            trackPlacementCTA("final", { ...HOME_PRIMARY, id: "conversion_band_secondary" }, {
              surface: "home_conversion_band",
              variant: "secondary",
            })
          }
        />
      </Suspense>


      <SiteFooter />
    </div>
  );
};

export default Index;
